import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';

const CopyRouteModal = ({ show, handleClose, route, providers, fetchRoutes }) => {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [provider, setProvider] = useState('');
  const [originPortId, setOriginPortId] = useState('');
  const [destinationPortId, setDestinationPortId] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (route) {
      setOrigin(route.origin);
      setDestination(route.destination);
      setProvider(route.provider_id);
    }
  }, [route]);

  const fetchPortSuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/ports', { params: { query: value } });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching port suggestions:', error);
    }
  };

  const handleCreateCopy = async () => {
    try {
      const response = await axios.post('/api/routes/copy', {
        origin: originPortId || route.origin_port_id,
        destination: destinationPortId || route.destination_port_id,
        provider,
        sourceRouteId: route.provider_guide_id,
      });

      if (response.status === 201) {
        setSuccessMessage('Routing Guide was copied successfully');
        setTimeout(() => {
          setSuccessMessage('');
          handleClose(); // Close the modal after showing success message
          fetchRoutes();
        }, 2000);
      }
    } catch (error) {
      if (error.response && error.response.status === 400 && error.response.data === 'Route already exists') {
        setError('Routing Guide must be unique');
      } else {
        setError('Copy action failed. Please try again.');
      }
    }
  };

  const inputProps = (name, value, onChange) => ({
    placeholder: `Enter ${name}`,
    value: value || '', // Ensure value is always a string
    onChange
  });

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Copy Routing Guide</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {successMessage ? (
          <Alert variant="success">{successMessage}</Alert>
        ) : (
          <>
            <Form.Group controlId="origin">
              <Form.Label>Origin</Form.Label>
              <Autosuggest
                suggestions={originSuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setOriginSuggestions)}
                onSuggestionsClearRequested={() => setOriginSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion.port_name}
                renderSuggestion={(suggestion) => <div>{suggestion.port_name}</div>}
                inputProps={inputProps('origin', origin, (e, { newValue }) => setOrigin(newValue))}
                onSuggestionSelected={(e, { suggestion }) => setOriginPortId(suggestion.port_id)}
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
            <Form.Group controlId="destination">
              <Form.Label>Destination</Form.Label>
              <Autosuggest
                suggestions={destinationSuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setDestinationSuggestions)}
                onSuggestionsClearRequested={() => setDestinationSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion.port_name}
                renderSuggestion={(suggestion) => <div>{suggestion.port_name}</div>}
                inputProps={inputProps('destination', destination, (e, { newValue }) => setDestination(newValue))}
                onSuggestionSelected={(e, { suggestion }) => setDestinationPortId(suggestion.port_id)}
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
            <Form.Group controlId="provider">
              <Form.Label>Provider</Form.Label>
              <Form.Control
                as="select"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              >
                {providers
                  .filter((p) => p.provider_id !== route.provider_id)
                  .map((p) => (
                    <option key={p.provider_id} value={p.provider_id}>
                      {p.provider_name}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {successMessage ? (
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateCopy}>
              Create Copy
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CopyRouteModal;
